/* eslint-disable no-param-reassign */
import { InspectionValues, StepTypes } from '@parallel-fluidics/shared/constants';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { stepType } from 'utils/reactFlowUtils';

import { useTheme } from '../../features/theme/themeProvider';
import { useSelection } from './selectionProvider';

export const chipWidth = 10; // passed to CSS as --_chipWidth in node.jsx
export const chipHeight = 115; // passed to CSS as --_chipHeight in node.jsx
const chipDatumType = PropTypes.shape({
  travelerId: PropTypes.string,
  prevInspection: PropTypes.string,
  curInspection: PropTypes.string,
  runStartDate: PropTypes.instanceOf(Date),
  runEndDate: PropTypes.instanceOf(Date),
  press: PropTypes.string,
  cavity: PropTypes.string,
  runID: PropTypes.number,
  failureTags: PropTypes.arrayOf(PropTypes.string),
  inspectedNote: PropTypes.string,
  shipped: PropTypes.bool,
});
export const projectStatusType = PropTypes.shape({
  passed: PropTypes.number,
  toBeInspected: PropTypes.number,
  onDeck: PropTypes.number,
  rejectCosmetic: PropTypes.number,
  rejectMajor: PropTypes.number,
  tuning: PropTypes.number,
  unknown: PropTypes.number,
  stepYield: PropTypes.number,
  chipData: PropTypes.arrayOf(chipDatumType),
  type: PropTypes.string,
});
export const queryStatusType = PropTypes.oneOf(['loading', 'success', 'error']);

export default function Chips({
  node,
  queryStatus,
  projectStatus = undefined,
}) {
  if (queryStatus === 'success') {
    return (
      <div className="chipsContainer">
        <ActualChips
          node={node}
          projectStatus={projectStatus}
          chipWidth={chipWidth}
          chipHeight={chipHeight}
        />
      </div>
    );
  }

  return (
    <>
      <ul className="data">
        <li>
          <DatumWithLabel status="passed" singular={projectStatus?.passed === 1}>
            <span className="emphasize">-</span>
            /
            {node.goalSum}
          </DatumWithLabel>
        </li>
      </ul>
      <div className="chipsContainer">
        <ol className="chips">
          {(new Array(node.goalSum).fill(null)).map((_, index) => (
            <li
              className="chip placeholder"
              // eslint-disable-next-line react/no-array-index-key
              key={`${node.uniqueId}-placeholder-${index}`}
            />
          ))}
        </ol>
      </div>
    </>
  );
}

Chips.propTypes = {
  node: stepType.isRequired,
  queryStatus: queryStatusType.isRequired,
  projectStatus: projectStatusType,
};

const labels = {
  passed: { singular: 'passed', plural: 'passed' },
  toBeInspected: { singular: 'to be inspected', plural: 'to be inspected' },
  onDeck: { singular: 'on deck', plural: 'on deck' },
  rejectCosmetic: { singular: 'cosmetic reject', plural: 'cosmetic rejects' },
  rejectMajor: { singular: 'major reject', plural: 'major rejects' },
  tuning: { singular: 'tuning', plural: 'tuning' },
};

function DatumWithLabel({
  status,
  singular = false,
  children,
}) {
  return (
    <p className="datumWithLabel">
      <span className={`icon ${status}`} />
      <span className="text">
        {children}
        <span className="label">
          {singular ? labels[status].singular : labels[status].plural}
        </span>
      </span>
    </p>
  );
}

DatumWithLabel.propTypes = {
  status: PropTypes.oneOf([
    'passed',
    'toBeInspected',
    'onDeck',
    'rejectCosmetic',
    'rejectMajor',
    'tuning',
  ]).isRequired,
  singular: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

function ActualChips({
  node,
  projectStatus,
}) {
  const totalChips = projectStatus.chipData.length;
  const { selectAll, activeNode, setSelectedRange } = useSelection();
  const isActive = useMemo(
    () => activeNode === node.uniqueId,
    [activeNode, node.uniqueId],
  );

  const remainingPassedChips = useMemo(
    () => Math.max(0, node.goalSum - projectStatus.passed),
    [node.goalSum, projectStatus.passed],
  );
  const remainingChips = useMemo(
    () => Math.max(
      0,
      node.goalSum - projectStatus.passed - projectStatus.onDeck - projectStatus.toBeInspected,
    ),
    [node.goalSum, projectStatus.passed, projectStatus.onDeck, projectStatus.toBeInspected],
  );

  const [rangeIdx, setRangeIdx] = useState({ start: 0, end: 0 });
  const [countData, setCountData] = useState({ cycles: -1 });
  const [averageProcessTime, setAverageProcessTime] = useState(-1);
  const [avgGap, setAvgGap] = useState(-1);
  const [estimate, setEstimate] = useState(-1);
  const [yieldRate, setYieldRate] = useState(-1);

  // -1 means no pointer is currently captured
  const [draggingPointer, setDraggingPointer] = useState(-1);
  const [draggingStart, setDraggingStart] = useState(false);
  const [draggingEnd, setDraggingEnd] = useState(false);
  const [draggingInvalid, setDraggingInvalid] = useState(false);

  const containerRef = useRef();

  useEffect(() => {
    if (!isActive || !selectAll) return;
    setRangeIdx({ start: 0, end: totalChips - 1 });
  }, [isActive, selectAll, totalChips]);

  const calculateAverage = useCallback((processLogs, travelersPerLog, stepYield) => {
    if (processLogs.length === 0 || !isActive) return;
    // group processLogs by Date
    const {
      duration, durationCount, gap, gapCount,
    } = processLogs.reduce((acc, log) => {
      const {
        press, start, end,
      } = log;
      const key = `${start.getMonth() + 1}/${start.getDate()}/${start.getFullYear()}`;
      const prevLog = acc[press] || {};
      if (end) {
        acc.duration += end - start;
        acc.durationCount += travelersPerLog;
      }
      if (prevLog.end && prevLog.key === key) {
        acc.gap += start - prevLog.end;
        acc.gapCount += travelersPerLog;
      }

      acc[press] = {
        end: end || null,
        key,
      };
      return acc;
    }, {
      duration: 0, durationCount: 0, gap: 0, gapCount: 0,
    });

    const avgPress = duration / (durationCount || 1) / 1000 / 60;
    const averageGap = gap / (gapCount || 1) / 1000 / 60;
    setAverageProcessTime(Math.round(avgPress));
    setAvgGap(Math.round(averageGap));
    setEstimate(
      projectStatus?.passed >= node.goalSum
        ? -1
        : Math.round((avgPress + averageGap) * (remainingPassedChips / ((stepYield / 100) || 1))),
    );
    setSelectedRange({
      cycles: countData.cycles,
      averageProcessTime,
      avgGap,
      estimate,
      yieldRate,
    });
  }, [
    remainingPassedChips,
    countData.cycles,
    averageProcessTime,
    avgGap,
    estimate,
    projectStatus?.passed,
    node.goalSum,
    yieldRate,
    isActive,
    setSelectedRange,
  ]);

  const groupByDate = useMemo(() => {
    const groupedData = projectStatus.chipData.reduce((acc, chip, index) => {
      // Note: Firestore timestamps can be null for newly created documents on the first write,
      // and will populate after a short delay (milliseconds).
      if (!chip.sortDate) {
        return acc;
      }
      const date = chip.sortDate.toDate();
      const key = `${date.getMonth() + 1}/${date.getDate()}`;
      if (!acc[key]) {
        acc[key] = { startIdx: index, endIdx: index };
      } else {
        acc[key].endIdx = index;
      }
      return acc;
    }, {});

    const groupedArray = Object.keys(groupedData).map((key) => ({
      date: key,
      ...groupedData[key],
    }));

    if ([StepTypes.MOLDING, StepTypes.BONDING].includes(projectStatus.type)) {
      let endGroupIndex = groupedArray.length - 1;
      let rangeEnd = groupedArray[endGroupIndex]?.endIdx || 0;
      let endFound = false;

      // Find endIdx by looking for the last non-"On deck" chip
      while (!endFound && endGroupIndex >= 0) {
        const group = groupedArray[endGroupIndex];
        for (let j = group.endIdx; j >= group.startIdx; j -= 1) {
          if (projectStatus.chipData[j].curInspection !== 'On deck') {
            rangeEnd = j;
            endFound = true;
            break;
          }
        }
        if (!endFound) {
          endGroupIndex -= 1;
        }
      }

      // After finding rangeEnd, determine rangeStart to meet minimum length
      let rangeStart = groupedArray[endGroupIndex]?.startIdx || 0;
      const minGroupLength = 5; // Minimum length for the range
      let totalLength = rangeEnd - rangeStart;
      endGroupIndex -= 1;

      // Expand start range backwards to meet minimum length if needed
      while (totalLength < minGroupLength && endGroupIndex >= 0) {
        rangeStart = groupedArray[endGroupIndex].startIdx;
        totalLength = rangeEnd - rangeStart;
        endGroupIndex -= 1;
      }
      setRangeIdx({ start: rangeStart, end: rangeEnd });
    }
    return groupedArray;
  }, [projectStatus.chipData, projectStatus.type]);

  useEffect(() => {
    if (rangeIdx.end - rangeIdx.start < 1) return;
    // calculate average process time by start to start
    const processMap = {};
    let maxTravelersPerLog = 0;
    const count = projectStatus.chipData.slice(rangeIdx.start, rangeIdx.end + 1)
      .reduce((acc, curChip) => {
        const {
          processId, runStartDate, runEndDate, press, curInspection, prevInspection,
        } = curChip;

        // calculate yield
        if (prevInspection === InspectionValues.PASSED) {
          if (curInspection === InspectionValues.PASSED) {
            acc.passed += 1;
          } else if (curInspection === InspectionValues.REJECT_COSMETIC) {
            acc.rejectCosmetic += 1;
          } else if (curInspection === InspectionValues.REJECT_MAJOR) {
            acc.rejectMajor += 1;
          } else if (curInspection === InspectionValues.TUNING) {
            acc.tuning += 1;
          } else if (curInspection === InspectionValues.NOT_INSPECTED) {
            acc.toBeInspected += 1;
          } else if (curInspection === 'On deck') {
            acc.onDeck += 1;
          } else {
            acc.unknown += 1;
          }
        } else if (curInspection === InspectionValues.TUNING) {
          acc.tuning += 1;
        }
        if (!runStartDate) return acc; // Skip if runStartDate is undefined

        if (!processMap[processId]) {
          processMap[processId] = {
            press,
            start: runStartDate,
            end: runEndDate,
            travelersCount: 1,
          };
          acc.cycles += 1;
        } else {
          processMap[processId].travelersCount += 1;
        }
        maxTravelersPerLog = Math.max(
          maxTravelersPerLog,
          processMap[processId].travelersCount,
        );

        return acc;
      }, {
        passed: 0,
        toBeInspected: 0,
        onDeck: 0,
        rejectCosmetic: 0,
        rejectMajor: 0,
        tuning: 0,
        unknown: 0,
        cycles: 0,
      });
    setCountData(count);
    const stepYield = Math.round(
      (count.passed / ((count.passed + count.rejectCosmetic + count.rejectMajor) || 1)) * 100,
    );
    setYieldRate(stepYield);
    calculateAverage(Object.values(processMap), maxTravelersPerLog, stepYield);
  }, [
    rangeIdx.start,
    rangeIdx.end,
    projectStatus,
    remainingPassedChips,
    calculateAverage,
  ]);

  const handleUpdateRange = useCallback((value, type) => {
    setRangeIdx((prev) => ({
      ...prev,
      [type]: value,
    }));
  }, []);

  const startDragging = useCallback((event, index) => {
    if (index !== rangeIdx.start && index !== rangeIdx.end) return;

    if (draggingPointer !== -1 && draggingPointer !== event.pointerId) {
      // there is a pointer that is already dragging
      // release its capture first
      containerRef.current.releasePointerCapture(draggingPointer);
    }

    // capture pointer
    containerRef.current.setPointerCapture(event.pointerId);
    setDraggingPointer(event.pointerId);

    // deselect current selection
    const selection = document.getSelection();
    selection.empty();

    if (index === rangeIdx.start) {
      setDraggingStart(true);
      setDraggingEnd(false);
    } else if (index === rangeIdx.end) {
      setDraggingStart(false);
      setDraggingEnd(true);
    }
  }, [rangeIdx.start, rangeIdx.end, draggingPointer]);

  const handlePointerMove = useCallback((event) => {
    if (
      (!draggingStart && !draggingEnd)
      || draggingPointer !== event.pointerId
    ) return;

    // get chip index
    const chipsInRow = Math.floor(containerRef.current.getBoundingClientRect().width / chipWidth);
    const columnIndex = Math.min(
      Math.max(Math.floor(event.nativeEvent.offsetX / chipWidth), 0),
      chipsInRow - 1,
    );
    const rowIndex = Math.min(
      Math.max(Math.floor(event.nativeEvent.offsetY / chipHeight), 0),
      Math.ceil(totalChips / chipsInRow) - 1,
    );
    const chipIndex = Math.min(
      rowIndex * chipsInRow + columnIndex,
      totalChips - 1,
    );

    // validate chip index
    if (
      (draggingStart && chipIndex === rangeIdx.end)
      || (draggingEnd && chipIndex === rangeIdx.start)
    ) {
      setDraggingInvalid(true);
      return;
    }
    setDraggingInvalid(false);

    if (draggingStart && chipIndex > rangeIdx.end) {
      // flip start and end
      handleUpdateRange(rangeIdx.end, 'start');
      handleUpdateRange(chipIndex, 'end');
      setDraggingStart(false);
      setDraggingEnd(true);
    } else if (draggingStart) {
      handleUpdateRange(chipIndex, 'start');
    } else if (draggingEnd && chipIndex < rangeIdx.start) {
      // flip start and end
      handleUpdateRange(rangeIdx.start, 'end');
      handleUpdateRange(chipIndex, 'start');
      setDraggingEnd(false);
      setDraggingStart(true);
    } else if (draggingEnd) {
      handleUpdateRange(chipIndex, 'end');
    }
  }, [
    draggingStart,
    draggingEnd,
    draggingPointer,
    rangeIdx.start,
    rangeIdx.end,
    totalChips,
    handleUpdateRange,
  ]);

  const handlePointerUp = useCallback((event) => {
    if (
      (!draggingStart && !draggingEnd)
      || draggingPointer !== event.pointerId
    ) return;

    // release pointer
    containerRef.current.releasePointerCapture(event.pointerId);
    setDraggingPointer(-1);

    setDraggingStart(false);
    setDraggingEnd(false);
    setDraggingInvalid(false);
  }, [draggingStart, draggingEnd, draggingPointer]);

  return (
    <>
      <ul className="data">
        <li>
          <DatumWithLabel status="passed" singular={projectStatus?.passed === 1}>
            <span className="emphasize">{projectStatus.passed}</span>
            {isActive && countData.passed > 0 && (
              <span className="selectedStatus">{countData.passed}</span>
            )}
            /
            {node.goalSum}
          </DatumWithLabel>
        </li>
        {['rejectCosmetic', 'rejectMajor', 'tuning', 'toBeInspected', 'onDeck'].map(
          (datum) => {
            if (projectStatus[datum]) {
              return (
                <li key={`${node.uniqueId}-${datum}`}>
                  <DatumWithLabel status={datum} singular={projectStatus[datum] === 1}>
                    <span className="emphasize">{projectStatus[datum]}</span>
                    {isActive && countData[datum] > 0 && (
                      <span className="selectedStatus">{countData[datum]}</span>
                    )}
                  </DatumWithLabel>
                </li>
              );
            }
            return undefined;
          },
        )}
      </ul>

      <div className="chipsContainer">
        <ol
          ref={containerRef}
          className={`
            chips
            ${([StepTypes.MOLDING, StepTypes.BONDING].includes(projectStatus.type)) ? ' rangeSlider' : ''}
            ${draggingStart ? ' draggingStart' : ''}
            ${draggingEnd ? ' draggingEnd' : ''}
            ${draggingInvalid ? ' invalid' : ''}
          `}
          onPointerMove={(e) => handlePointerMove(e)}
          onPointerUp={(e) => handlePointerUp(e)}
          onPointerCancel={(e) => handlePointerUp(e)}
        >
          {groupByDate.map(({ startIdx, endIdx, date }, olIdx) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${node.title}_${olIdx}`}>
              {projectStatus.chipData.slice(startIdx, endIdx + 1).map((chipDatum, index) => (
                <Chip
                  chipDatum={chipDatum}
                  uniqueId={node.uniqueId}
                  onPointerDown={(e) => startDragging(e, startIdx + index)}
                  isHighlighted={
                    rangeIdx.start <= (startIdx + index)
                    && (startIdx + index) <= rangeIdx.end
                  }
                  isRangeStart={(startIdx + index) === rangeIdx.start}
                  isRangeEnd={(startIdx + index) === rangeIdx.end}
                  isFirst={index === 0}
                  isLast={index === endIdx - startIdx}
                  date={date}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${node.title}_${index}`}
                />
              ))}
            </React.Fragment>
          ))}
          {(new Array(remainingChips).fill({})).map((chipDatum, index) => (
            <Chip
              // eslint-disable-next-line react/no-array-index-key
              key={`${node.title}_${index}`}
              chipDatum={chipDatum}
              uniqueId={node.uniqueId}
            />
          ))}
        </ol>
      </div>
    </>
  );
}

ActualChips.propTypes = {
  node: stepType.isRequired,
  projectStatus: projectStatusType.isRequired,
};

function Chip({
  chipDatum,
  uniqueId,
  onPointerDown = null,
  isHighlighted = false,
  isRangeStart = false,
  isRangeEnd = false,
  isFirst = false,
  isLast = false,
  date = null,
}) {
  const { isDarkMode } = useTheme();

  const getClassName = (inspection, noRunEndDate = false) => {
    switch (inspection) {
      case InspectionValues.NOT_INSPECTED:
        if (noRunEndDate) {
          return ' toBeInspected inProgress';
        }
        return ' toBeInspected';
      case InspectionValues.PASSED:
        return ' passed completed';
      case InspectionValues.REJECT_MAJOR:
        return ' rejectMajor completed';
      case InspectionValues.REJECT_COSMETIC:
        return ' rejectCosmetic completed';
      case InspectionValues.TUNING:
        return ' tuning completed';
      case 'On deck':
        return ' onDeck';
      case 'Nonexistent':
        return ' nonexistent';
      default:
        return '';
    }
  };
  return (
    <li
      className={`
          chip
          ${isHighlighted ? ' highlighted' : ''}
          ${isRangeStart ? ' start' : ''}
          ${isRangeEnd ? ' end' : ''}
          ${isFirst ? ' first' : ''}
          ${isLast ? ' last' : ''}
          ${date ? ' hasDate' : ''}
        `}
    >
      {chipDatum.travelerId ? (
        <>
          <div
            className="rangeIndicator"
            onPointerDown={(e) => {
              onPointerDown(e);
            }}
          />
          <Link
            to={`/travelers/${chipDatum.travelerId}`}
            target="_blank"
            data-tooltip-id={`tooltip-${uniqueId}-${chipDatum.travelerId}`}
            className="statusWrapper"
          >
            <div className="prevStatus">
              <div className={getClassName(chipDatum.prevInspection)} />
            </div>
            <div className="curStatus">
              <div className={getClassName(chipDatum.curInspection, !chipDatum.runEndDate)}>
                <p className="visuallyHidden">{chipDatum.travelerId}</p>
              </div>
            </div>
          </Link>
          {(
            chipDatum.inspectedNote
            || chipDatum.failureTags?.length > 0
            || chipDatum.runStartDate
          ) && (
            <Tooltip id={`tooltip-${uniqueId}-${chipDatum.travelerId}`} style={{ zIndex: 5 }} opacity={1} variant={isDarkMode ? 'light' : 'dark'}>
              <div>
                {chipDatum?.runStartDate && (
                  <p>
                    <span>Run Start Date: </span>
                    <span>{chipDatum?.runStartDate?.toLocaleString() || 'N/A'}</span>
                  </p>
                )}
                {chipDatum?.runEndDate && (
                  <p>
                    <span>Run End Date: </span>
                    <span>{chipDatum?.runEndDate?.toLocaleString() || 'N/A'}</span>
                  </p>
                )}
                {chipDatum.press && (
                  <p>
                    <span>Press: </span>
                    <span>{chipDatum.press}</span>
                  </p>
                )}
                {chipDatum.runID && (
                  <p>
                    <span>Run ID: </span>
                    <span>{chipDatum.runID}</span>
                  </p>
                )}
                {chipDatum.cavity && (
                  <p>
                    <span>Cavity: </span>
                    <span>{chipDatum.cavity}</span>
                  </p>
                )}
                {chipDatum.failureTags?.length > 0 && (
                  <p>
                    <span>Failure Tags: </span>
                    <span>{chipDatum.failureTags?.join(', ')}</span>
                  </p>
                )}
                {chipDatum.inspectedNote && (
                  <p>
                    <span>Inspected Note: </span>
                    <span>{chipDatum.inspectedNote}</span>
                  </p>
                )}
                {chipDatum.shipped && (
                  <p>
                    <span>Shipped: </span>
                    <span>{chipDatum.shipped ? 'Yes' : 'No'}</span>
                  </p>
                )}
              </div>
            </Tooltip>
          )}
          {isFirst && date && (
            <p className="date" title={date}>{date}</p>
          )}
          <div className="overlay" />
        </>
      ) : (
        <>
          <div className="rangeIndicator nonexistent" />
          <div className="statusWrapper">
            <div className="prevStatus">
              <div className="nonexistent">
                <p className="visuallyHidden">Nonexistent chip</p>
              </div>
            </div>
          </div>
        </>
      )}
    </li>
  );
}

Chip.propTypes = {
  chipDatum: chipDatumType.isRequired,
  uniqueId: PropTypes.string.isRequired,
  onPointerDown: PropTypes.func,
  isHighlighted: PropTypes.bool,
  isRangeStart: PropTypes.bool,
  isRangeEnd: PropTypes.bool,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  date: PropTypes.string,
};
